import React, { useState, useEffect } from 'react';
import './News.css';

const News: React.FC = () => {
  // State for active category and animation trigger
  const [activeCategory, setActiveCategory] = useState('all');
  const [animate, setAnimate] = useState(false);
  const [isReportExpanded, setIsReportExpanded] = useState(false);
  
  // Function to handle category selection
  const handleCategoryClick = (category: string) => {
    if (category !== activeCategory) {
      setActiveCategory(category);
      setAnimate(true);
      
      // Scroll to top when changing categories
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  
  // Toggle AFC report expanded state
  const toggleReportExpand = () => {
    setIsReportExpanded(!isReportExpanded);
  };

  // Reset animation flag
  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => {
        setAnimate(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [animate]);

  return (
    <section className="news">
      <div className="news-header">
        <h2>Market Insights & Updates</h2>
        <div className="header-underline"></div>
        <p>Get the latest market intelligence and Finance Club announcements</p>
      </div>
      
      {/* AFC Report Section */}
      <div className="afc-report-section">
        <div className="afc-report-container">
          <div className="afc-report-header">
            <div className="afc-report-title">
              <div className="report-icon">
                <span className="report-icon-symbol">📊</span>
              </div>
              <h2>AFC Financial Report</h2>
            </div>
            <div className="afc-report-description">
              <p>Access our latest financial analysis and market insights curated by Adelphi Finance Club members.</p>
            </div>
          </div>
          
          <div className="afc-report-preview">
            <div className="report-thumbnail">
              <img src="https://placehold.co/120x150/4F2C1D/FFF.png?text=AFC+Report" alt="AFC Report thumbnail" />
              <div className="report-badge">LATEST</div>
            </div>
            
            <div className="report-info">
              <h3>Q1 2025 Financial Outlook</h3>
              <p>Comprehensive analysis of market trends, sector performance, and investment opportunities for the first quarter of 2025.</p>
              
              <div className="report-meta">
                <div className="report-date">
                  <span className="meta-icon date-icon"></span>
                  <span>March 15, 2025</span>
                </div>
                <div className="report-pages">
                  <span className="meta-icon pages-icon"></span>
                  <span>24 pages</span>
                </div>
              </div>
              
              <div className="report-authors">
                <div className="authors-label">Prepared by:</div>
                <div className="authors-list">
                  <span>AFC Research Team</span>
                </div>
              </div>
            </div>
            
            <div className="report-actions">
              <a href="https://drive.google.com/drive/folders/196PF15_OkVXdtDgevHdGQZSWzJHyQZRC" target="_blank" rel="noopener noreferrer" className="download-button">
                <span>Download Report</span>
                <span className="download-icon"></span>
              </a>
              <button className="preview-button" onClick={toggleReportExpand}>
                <span>{isReportExpanded ? 'Hide Preview' : 'Quick Preview'}</span>
                <span className={`preview-icon ${isReportExpanded ? 'expanded' : ''}`}></span>
              </button>
            </div>
          </div>
          
          {isReportExpanded && (
            <div className="afc-report-expanded">
              <div className="report-highlights">
                <h4>Report Highlights</h4>
                <ul className="highlights-list">
                  <li>
                    <span className="highlight-marker"></span>
                    <div className="highlight-content">
                      <strong>Market Outlook:</strong> Analysis of current market conditions and forecasted trends for Q1 2025.
                    </div>
                  </li>
                  <li>
                    <span className="highlight-marker"></span>
                    <div className="highlight-content">
                      <strong>Sector Performance:</strong> Detailed breakdown of performance across major market sectors.
                    </div>
                  </li>
                  <li>
                    <span className="highlight-marker"></span>
                    <div className="highlight-content">
                      <strong>Investment Strategies:</strong> Recommendations for portfolio positioning in the current economic climate.
                    </div>
                  </li>
                  <li>
                    <span className="highlight-marker"></span>
                    <div className="highlight-content">
                      <strong>Risk Analysis:</strong> Evaluation of potential risks and volatility factors affecting market performance.
                    </div>
                  </li>
                </ul>
              </div>
              
              <div className="report-preview-images">
                <div className="preview-image">
                  <img src="https://placehold.co/300x200/FFB500/4F2C1D.png?text=Executive+Summary" alt="Report preview page 1" />
                  <div className="preview-caption">Executive Summary</div>
                </div>
                <div className="preview-image">
                  <img src="https://placehold.co/300x200/FFB500/4F2C1D.png?text=Market+Performance" alt="Report preview page 2" />
                  <div className="preview-caption">Market Performance</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="featured-news">
        <div className="featured-news-content">
          <div className="news-tag">FEATURED</div>
          <h3>2025 Economic Outlook: Navigating the New Financial Landscape</h3>
          <p>A comprehensive analysis of emerging market trends, interest rate projections, and sector-specific forecasts for the upcoming year. Our financial experts break down what to expect and how to position for success.</p>
          <div className="news-meta">
            <span className="news-date">March 10, 2025</span>
            <a href="https://www.marketwatch.com/economy-politics/calendar" target="_blank" rel="noopener noreferrer" className="news-link">
              <span>Read Full Analysis</span>
              <span className="arrow-icon">→</span>
            </a>
          </div>
        </div>
        <div className="featured-news-image">
          <img src="https://placehold.co/600x400/4F2C1D/FFB500.png?text=Economic+Outlook" alt="Economic outlook" />
          <div className="image-overlay"></div>
        </div>
      </div>
      
      <div className="news-categories">
        <button 
          className={`category ${activeCategory === 'all' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('all')}
        >
          All Updates
        </button>
        <button 
          className={`category ${activeCategory === 'market' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('market')}
        >
          Market News
        </button>
        <button 
          className={`category ${activeCategory === 'club' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('club')}
        >
          Club Announcements
        </button>
        <button 
          className={`category ${activeCategory === 'industry' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('industry')}
        >
          Industry Insights
        </button>
      </div>
      
      <div className={`news-grid ${animate ? 'fade-in' : ''}`}>
        <div className="news-card">
          <div className="card-image">
            <div className="news-tag">MARKETS</div>
            <img src="https://placehold.co/400x250/4F2C1D/FFB500.png?text=Tech+Sector" alt="Market trends" />
          </div>
          <div className="news-content">
            <div className="content-meta">
              <span className="date">MAR 12, 2025</span>
              <span className="reading-time">5 MIN READ</span>
            </div>
            <h3>Tech Sector Performance: Q1 2025 Review</h3>
            <p>Analysis of technology stock performance, key innovations, and emerging trends shaping the digital economy.</p>
            <a href="https://www.marketwatch.com/economy-politics/calendar" target="_blank" rel="noopener noreferrer" className="read-more">
              <span>Continue Reading</span>
              <span className="arrow">→</span>
            </a>
          </div>
        </div>
        
        <div className="news-card">
          <div className="card-image">
            <div className="news-tag">CLUB NEWS</div>
            <img src="https://placehold.co/400x250/4F2C1D/FFB500.png?text=Finance+Panel" alt="Club event" />
          </div>
          <div className="news-content">
            <div className="content-meta">
              <span className="date">MAR 8, 2025</span>
              <span className="reading-time">3 MIN READ</span>
            </div>
            <h3>Finance Leadership Panel: Key Takeaways</h3>
            <p>Highlights from our recent leadership panel featuring executives from top financial institutions discussing career development and industry trends.</p>
            <a href="https://www.marketwatch.com/economy-politics/calendar" target="_blank" rel="noopener noreferrer" className="read-more">
              <span>Continue Reading</span>
              <span className="arrow">→</span>
            </a>
          </div>
        </div>
        
        <div className="news-card">
          <div className="card-image">
            <div className="news-tag">ANALYSIS</div>
            <img src="https://placehold.co/400x250/4F2C1D/FFB500.png?text=Banking+Sector" alt="Banking sector" />
          </div>
          <div className="news-content">
            <div className="content-meta">
              <span className="date">MAR 5, 2025</span>
              <span className="reading-time">7 MIN READ</span>
            </div>
            <h3>Banking Sector Transformation: Digital Strategy and Risk Management</h3>
            <p>How traditional banks are adapting to fintech disruption through strategic partnerships and internal innovation.</p>
            <a href="https://www.marketwatch.com/economy-politics/calendar" target="_blank" rel="noopener noreferrer" className="read-more">
              <span>Continue Reading</span>
              <span className="arrow">→</span>
            </a>
          </div>
        </div>
      </div>
      
      <div className="market-data-widget">
        <div className="widget-header">
          <h3>Market Snapshot</h3>
          <p>Live data from global markets</p>
        </div>
        <div className="tradingview-widget-container">
          <div className="tradingview-widget-wrapper">
            {/* TradingView Widget BEGIN */}
            <div className="tradingview-widget-copyright">
              <a href="https://www.tradingview.com/" rel="noopener nofollow" target="_blank">
                <span className="blue-text">Track all markets on TradingView</span>
              </a>
            </div>
            <div className="tradingview-widget">
              <iframe 
                title="market-overview"
                src="https://s.tradingview.com/embed-widget/market-overview/?locale=en#%7B%22colorTheme%22%3A%22light%22%2C%22dateRange%22%3A%221D%22%2C%22showChart%22%3Atrue%2C%22largeChartUrl%22%3A%22%22%2C%22isTransparent%22%3Afalse%2C%22showSymbolLogo%22%3Atrue%2C%22showFloatingTooltip%22%3Afalse%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22520%22%2C%22plotLineColorGrowing%22%3A%22rgba(79%2C%2044%2C%2029%2C%201)%22%2C%22plotLineColorFalling%22%3A%22rgba(79%2C%2044%2C%2029%2C%201)%22%2C%22gridLineColor%22%3A%22rgba(240%2C%20240%2C%20240%2C%201)%22%2C%22scaleFontColor%22%3A%22rgba(120%2C%20123%2C%20134%2C%201)%22%2C%22belowLineFillColorGrowing%22%3A%22rgba(255%2C%20181%2C%200%2C%200.12)%22%2C%22belowLineFillColorFalling%22%3A%22rgba(255%2C%2081%2C%2081%2C%200.12)%22%2C%22belowLineFillColorGrowingBottom%22%3A%22rgba(41%2C%2098%2C%20255%2C%200)%22%2C%22belowLineFillColorFallingBottom%22%3A%22rgba(41%2C%2098%2C%20255%2C%200)%22%2C%22symbolActiveColor%22%3A%22rgba(79%2C%2044%2C%2029%2C%200.12)%22%2C%22tabs%22%3A%5B%7B%22title%22%3A%22Indices%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22FOREXCOM%3ASPXUSD%22%2C%22d%22%3A%22S%26P%20500%22%7D%2C%7B%22s%22%3A%22FOREXCOM%3ANASQUD%22%2C%22d%22%3A%22Nasdaq%20100%22%7D%2C%7B%22s%22%3A%22FOREXCOM%3ADJI%22%2C%22d%22%3A%22Dow%2030%22%7D%2C%7B%22s%22%3A%22INDEX%3ANKY%22%2C%22d%22%3A%22Nikkei%20225%22%7D%2C%7B%22s%22%3A%22INDEX%3ADEU40%22%2C%22d%22%3A%22DAX%20Index%22%7D%2C%7B%22s%22%3A%22FOREXCOM%3AUKXGBP%22%2C%22d%22%3A%22UK%20100%22%7D%5D%2C%22originalTitle%22%3A%22Indices%22%7D%2C%7B%22title%22%3A%22Commodities%22%2C%22symbols%22%3A%5B{%7B%22s%22%3A%22CME_MINI%3AES1!%22%2C%22d%22%3A%22S%26P%20500%22%7D%2C%7B%22s%22%3A%22CME%3A6E1!%22%2C%22d%22%3A%22Euro%22%7D%2C%7B%22s%22%3A%22COMEX%3AGC1!%22%2C%22d%22%3A%22Gold%22%7D%2C%7B%22s%22%3A%22NYMEX%3ACL1!%22%2C%22d%22%3A%22Crude%20Oil%22%7D%2C%7B%22s%22%3A%22NYMEX%3ANG1!%22%2C%22d%22%3A%22Natural%20Gas%22%7D%2C%7B%22s%22%3A%22CBOT%3AZC1!%22%2C%22d%22%3A%22Corn%22%7D%5D%2C%22originalTitle%22%3A%22Commodities%22%7D%2C%7B%22title%22%3A%22Bonds%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22CBOT%3AZB1!%22%2C%22d%22%3A%22T-Bond%22%7D%2C%7B%22s%22%3A%22CBOT%3AUB1!%22%2C%22d%22%3A%22Ultra%20T-Bond%22%7D%2C%7B%22s%22%3A%22EUREX%3AFGBL1!%22%2C%22d%22%3A%22Euro%20Bund%22%7D%2C%7B%22s%22%3A%22TREASURIES%3AUS10Y%22%2C%22d%22%3A%22US%2010Y%22%7D%2C%7B%22s%22%3A%22TREASURIES%3AUS30Y%22%2C%22d%22%3A%22US%2030Y%22%7D%2C%7B%22s%22%3A%22TREASURIES%3AUS02Y%22%2C%22d%22%3A%22US%202Y%22%7D%5D%2C%22originalTitle%22%3A%22Bonds%22%7D%2C%7B%22title%22%3A%22Forex%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22FX%3AEURUSD%22%2C%22d%22%3A%22EUR%2FUSD%22%7D%2C%7B%22s%22%3A%22FX%3AGBPUSD%22%2C%22d%22%3A%22GBP%2FUSD%22%7D%2C%7B%22s%22%3A%22FX%3AUSDJPY%22%2C%22d%22%3A%22USD%2FJPY%22%7D%2C%7B%22s%22%3A%22FX%3AUSDCHF%22%2C%22d%22%3A%22USD%2FCHF%22%7D%2C%7B%22s%22%3A%22FX%3AAUDUSD%22%2C%22d%22%3A%22AUD%2FUSD%22%7D%2C%7B%22s%22%3A%22FX%3AUSDCAD%22%2C%22d%22%3A%22USD%2FCAD%22%7D%5D%2C%22originalTitle%22%3A%22Forex%22%7D%5D%2C%22utm_source%22%3A%22www.example.com%22%2C%22utm_medium%22%3A%22widget%22%2C%22utm_campaign%22%3A%22market-overview%22%7D"
                style={{
                  width: "100%",
                  height: "520px",
                  border: "none"
                }}
              ></iframe>
            </div>
            {/* TradingView Widget END */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default News;